import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    CopyToOfferingCategory,
    CopyToOfferingCategoryFailure,
    CopyToOfferingCategorySuccess,
    CreateCourseOffering,
    CreateCourseOfferingFailure,
    CreateCourseOfferingSuccess,
    CreateProgramOffering,
    CreateProgramOfferingFailure,
    CreateProgramOfferingSuccess,
    DeleteCourseOffering,
    DeleteCourseOfferingFailure,
    DeleteCourseOfferingSuccess,
    FetchCourseOffering,
    FetchCourseOfferingFailure,
    FetchCourseOfferingSuccess,
    SetCourseOffering,
    SetCourseOfferings,
    UnsetCourseOffering,
    UpdateOffering,
    UpdateOfferingCategory,
    UpdateOfferingCategoryFailure,
    UpdateOfferingCategorySuccess,
    UpdateOfferingFailure,
    UpdateOfferingOrder,
    UpdateOfferingOrderFailure,
    UpdateOfferingOrderSuccess,
    UpdateOfferingSuccess,
    UpdateProgramOfferingCompletion,
    UpdateProgramOfferingCompletionFailure,
    UpdateProgramOfferingCompletionSuccess,
} from '../actions';
import { AddCourseOfferings } from '../actions/add-entities.action';
import { CourseOfferingApiService } from '../services/course-offering.api-service';
import { CourseOffering } from './course-offering.model';
import { CourseOfferingStateModel } from './course-offering.state-model';

@State<CourseOfferingStateModel>({
    name: 'courseOffering',
    defaults: new CourseOfferingStateModel(),
})
@Injectable()
export class CourseOfferingState extends EntityState<CourseOffering> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: CourseOfferingStateModel, id: string = state.item): CourseOffering {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: CourseOfferingStateModel): CourseOffering[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(CourseOfferingState.sortProperties));

        return toReturn;
    }

    constructor(private _courseOfferingApiService: CourseOfferingApiService) {
        super(null, 0);
    }

    @Action(AddCourseOfferings)
    addCourseOfferingsEntities(ctx: StateContext<CourseOfferingStateModel>, action: AddCourseOfferings): void {
        this._addEntities(ctx, action);
    }

    @Action(SetCourseOffering)
    setEntity(ctx: StateContext<CourseOfferingStateModel>, action: SetCourseOffering): void {
        this._setEntity(ctx, action, AddCourseOfferings);
    }

    @Action(UnsetCourseOffering)
    unsetEntity(ctx: StateContext<CourseOfferingStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateCourseOffering)
    createEntity(ctx: StateContext<CourseOfferingStateModel>, action: CreateCourseOffering): void {
        this._courseOfferingApiService.create(action.payload).subscribe(
            (courseOffering) => {
                this._addEntities(ctx, { payload: [courseOffering] });

                ctx.dispatch([new CreateCourseOfferingSuccess(courseOffering)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new CreateCourseOfferingFailure({ response }));
            }
        );
    }

    @Action(CreateProgramOffering)
    createProgramEntity(ctx: StateContext<CourseOfferingStateModel>, action: CreateProgramOffering): void {
        this._courseOfferingApiService.createProgramOffering(action.payload).subscribe(
            (courseOfferingGroup) => {
                this._addEntities(ctx, { payload: [courseOfferingGroup] });

                ctx.dispatch([new CreateProgramOfferingSuccess(courseOfferingGroup)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new CreateProgramOfferingFailure({ response }));
            }
        );
    }

    @Action(UpdateOffering)
    updateEntity(ctx: StateContext<CourseOfferingStateModel>, action: UpdateOffering): void {
        this._updateEntity(ctx, action, UpdateOfferingSuccess, UpdateOfferingFailure, AddCourseOfferings, this._courseOfferingApiService.update(action.payload)).then(() => {});
    }

    @Action(UpdateOfferingOrder)
    updateOfferingOrder(ctx: StateContext<CourseOfferingStateModel>, action: UpdateOffering): void {
        this._updateEntity(
            ctx,
            action,
            UpdateOfferingOrderSuccess,
            UpdateOfferingOrderFailure,
            AddCourseOfferings,
            this._courseOfferingApiService.updateOfferingOrder(action.payload)
        ).then(() => {});
    }

    @Action(UpdateProgramOfferingCompletion)
    updateProgramOfferingCompletion(ctx: StateContext<CourseOfferingStateModel>, action: UpdateProgramOfferingCompletion): void {
        this._updateEntity(
            ctx,
            action,
            UpdateProgramOfferingCompletionSuccess,
            UpdateProgramOfferingCompletionFailure,
            AddCourseOfferings,
            this._courseOfferingApiService.updateProgramOfferingCompletion(action.payload)
        ).then(() => {});
    }

    @Action(DeleteCourseOffering)
    deleteEntity(ctx: StateContext<CourseOfferingStateModel>, action: DeleteCourseOffering): void {
        this._courseOfferingApiService.delete(action.payload).subscribe(
            (result) => {
                this._removeEntitiesFromState(ctx, [action.payload]);
                ctx.dispatch(new DeleteCourseOfferingSuccess(result));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new DeleteCourseOfferingFailure(action.payload));
            }
        );
    }

    @Action(FetchCourseOffering)
    fetchEntity(ctx: StateContext<CourseOfferingStateModel>, action: FetchCourseOffering): void {
        this._fetchEntity(
            ctx,
            action,
            FetchCourseOfferingSuccess,
            FetchCourseOfferingFailure,
            AddCourseOfferings,
            SetCourseOffering,
            this._courseOfferingApiService.read(action.payload)
        ).then(() => {});
    }

    @Action(FetchCourseOfferingSuccess)
    fetchEntitySuccess(ctx: StateContext<CourseOfferingStateModel>, action: FetchCourseOfferingSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchCourseOfferingFailure)
    fetchEntityFailure(ctx: StateContext<CourseOfferingStateModel>, action: FetchCourseOfferingFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetCourseOfferings)
    setCourseOfferingEntities(ctx: StateContext<CourseOfferingStateModel>, action: SetCourseOfferings): void {
        this._setEntities(ctx, action);
    }

    @Action(UpdateOfferingCategory)
    updateOfferingCategory(ctx: StateContext<CourseOfferingStateModel>, { payload }: UpdateOfferingCategory): void {
        this._courseOfferingApiService.updateOfferingCategory(payload).subscribe(
            (entity) => {
                ctx.dispatch(new UpdateOfferingCategorySuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdateOfferingCategoryFailure());
            }
        );
    }

    @Action(CopyToOfferingCategory)
    copyToOfferingCategory({ dispatch }: StateContext<CourseOfferingStateModel>, { payload }: CopyToOfferingCategory): void {
        this._courseOfferingApiService.copyToOfferingCategory(payload.courseOfferingId, payload.targetCategoryId).subscribe(
            (entity) => {
                dispatch(new CopyToOfferingCategorySuccess({ id: entity.id, asyncJobId: entity.async_job_id }));
            },
            (response: HttpErrorResponse) => {
                dispatch(new CopyToOfferingCategoryFailure({ response }));
            }
        );
    }
}
